













































































































import { Vue, Component, Watch } from 'vue-property-decorator'
import { ExStore } from 'vuex'
import { StateChanger } from 'vue-infinite-loading'
import { EventEmitter } from 'events'
import { DeleteConfirmResults } from '@/constants/utils'
import { ImageFile } from '@/types/image'
import dayjs from 'dayjs'
import { isPlatformAndroid } from '@/utils/cordova'

enum EventTypes {
  CONFIRM_DELETE
}

@Component({
  filters: {
    formatDate(date: string) {
      return dayjs(date).format('M月D日 HH:mm')
    }
  },
  components: {
    FileInput: () => import('@/components/common/inputs/FileInput.vue'),
    InfiniteLoading: () => import('vue-infinite-loading'),
    SyncImage: () => import('@/components/imageTab/SyncImage.vue'),
    ImageModal: () => import('@/components/imageTab/ImageModal.vue'),
    Loading: () => import('@/components/common/loadings/Index.vue'),
    DeleteConfirmModal: () =>
      import('@/components/common/modals/AlertModal.vue'),
    FlushMessage: () => import('@/components/common/FlushMessage.vue')
  }
})
export default class ImageList extends Vue {
  $store!: ExStore
  thumbnailUrls: {
    [id: number]: string
  } = {}
  selectedImageId: number | null = null
  selectedImageUrl = ''
  fileSelect = false

  showDeleteConfirmModal = false
  events = new EventEmitter()

  @Watch('fileBase64')
  onChangeFile(base64: string) {
    if (!base64) return
    this.$router.push({ name: 'PatientImageCreate' })
  }

  get userInfo() {
    return this.$store.getters['user/userInfo']
  }

  get relativeInfo() {
    return this.$store.getters['user/relativeInfo']
  }

  get file() {
    return this.$store.getters['image/patientUploadImage']
  }

  set file(file: { name: string; base64: string }) {
    this.$store.dispatch('image/updatePatientUploadImage', file)
  }

  get fileName() {
    return this.file.name
  }

  set fileName(name: string) {
    this.file = { name, base64: this.fileBase64 }
  }

  get fileBase64() {
    return this.file.base64
  }

  set fileBase64(base64: string) {
    this.file = { name: this.fileName, base64 }
  }

  get patientId(): number | null | undefined {
    return this.userInfo.isPatientRelative
      ? this.relativeInfo?.patient.id
      : this.userInfo.id
  }

  get images() {
    return this.$store.getters['image/images']
  }

  get hasImageNext() {
    return this.$store.getters['image/hasImageNext']
  }

  get selectedImage() {
    return this.images.find(img => img.id === this.selectedImageId)
  }

  created() {
    const patientId = this.patientId
    if (patientId == null) return
    this.$store.dispatch('image/updateImages', { patientId })
    if (!this.userInfo.isPatientRelative) {
      this.$postMessage.addEventListener('activateApp', () => {
        this.$store.dispatch('image/updateImages', { patientId })
      })
    }
  }

  beforeDestroy() {
    for (const key in this.thumbnailUrls) {
      const url = this.thumbnailUrls[key]
      URL.revokeObjectURL(url)
    }
    URL.revokeObjectURL(this.selectedImageUrl)
  }

  handleFileInputClick() {
    if (this.userInfo.isPatientRelative) {
      this.$store.dispatch('alert/showMessages', ['このボタンは無効です'])
      return
    }
    if (isPlatformAndroid()) {
      this.$postMessage.showCameraView()
      return
    }
    this.fileSelect = true
  }

  getSenderName(image: ImageFile) {
    if (image.worker) {
      return `${image.worker.lastName}${image.worker.occupation.name}`
    }
    if (this.userInfo.isPatientRelative && this.relativeInfo)
      return `${this.relativeInfo.patient.lastName}${this.relativeInfo.patient.firstName}`
    return `${this.userInfo.lastName}${this.userInfo.firstName}`
  }

  async handleDelete(imageId: number) {
    if (this.userInfo.isPatientRelative) {
      this.$store.dispatch('alert/showMessages', ['このボタンは無効です'])
      return
    }
    this.showDeleteConfirmModal = true
    const resust = await new Promise<DeleteConfirmResults>(resolve =>
      this.events.once(EventTypes.CONFIRM_DELETE, resolve)
    )
    this.showDeleteConfirmModal = false

    if (resust === DeleteConfirmResults.CANCEL) {
      return
    }
    const patientId = this.patientId
    if (patientId == null) return
    await this.$store.dispatch('image/removeImage', {
      patientId,
      imageId
    })
  }

  handleEdit(imageId: number) {
    if (this.userInfo.isPatientRelative) {
      this.$store.dispatch('alert/showMessages', ['このボタンは無効です'])
      return
    }
    this.$router.push({
      name: 'PatientImageEdit',
      params: { imageId: String(imageId) }
    })
  }

  getThumbnailUrl(imageId: number) {
    return this.thumbnailUrls[imageId] || ''
  }

  handleCloseModal() {
    this.selectedImageId = null
    this.selectedImageUrl = ''
  }

  handleSelectImage(imageId: number) {
    this.selectedImageId = imageId
  }

  handleLoadNextImage($state: StateChanger) {
    const patientId = this.patientId
    if (patientId == null) return
    this.$store.dispatch('image/loadNextImages', { patientId }).then(() => {
      if (this.hasImageNext) {
        $state.loaded()
      } else {
        $state.complete()
      }
    })
  }

  handleDeleteConfirmOk() {
    this.events.emit(EventTypes.CONFIRM_DELETE, DeleteConfirmResults.OK)
  }

  handleDeleteConfirmCancel() {
    this.events.emit(EventTypes.CONFIRM_DELETE, DeleteConfirmResults.CANCEL)
  }

  async handleThumbnailCreated(imageId: number) {
    if (imageId in this.thumbnailUrls) return
    const patientId = this.patientId
    if (patientId == null) return
    const res = await this.$api.image.fetchThumbnail({
      patientId,
      imageId
    })
    const url = window.URL.createObjectURL(res)
    this.thumbnailUrls = { ...this.thumbnailUrls, [imageId]: url }
  }

  async handleImageCreated(imageId: number) {
    const patientId = this.patientId
    if (patientId == null) return
    const res = await this.$api.image.fetchImage({
      patientId,
      imageId
    })
    const url = window.URL.createObjectURL(res)
    this.selectedImageUrl = url
  }
}
